<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<div class="app-container" fxLayout="column" fxLayoutAlign="center stretch" fxFlexFill>
  @if (!hideTopbar) {
  <app-topbar [title]="titleText" [topbarView]="TopbarView.STEAMFITTER_HOME"></app-topbar>
  }

  @if (apiIsSick) {
  <div class="app-header-container elevate">
    <h1>{{ apiMessage }}</h1>
    <h2>Please refresh this page.</h2>
    <h2>If the problem persists, please contact the site administrator.</h2>
  </div>
  }

  @if (!apiIsSick && isAuthorizedUser && permissions) {
  <div class="main-area">
    <div class="top-row">
      <div class="one-cell last-cell">
        @if (canViewAdministration) {
        <button aria-label="Show Administration Page" mat-icon-button [routerLink]="['/admin']" style="outline: none;"
          title="Administration">
          <mat-icon class="mdi-24px center-self" fontIcon="mdi-cog"></mat-icon>
        </button>
        }
      </div>
    </div>
    <div class="content-container elevate">
      <div class="icon-text-container">
        <mat-icon class="steamfitter-icon" svgIcon="ic_crucible_steamfitter"></mat-icon>
        <button mat-button class="section-menu-trigger" [matMenuTriggerFor]="sectionMenu" style="outline: none;">
          <mat-icon fontIcon="mdi-chevron-down"></mat-icon>
          My {{ selectedSection }}
        </button>
        <mat-menu #sectionMenu="matMenu">
          @if (canViewScenarioList()) {
          <button mat-menu-item (click)="selectTab(section.scenarios)">{{ section.scenarios }}</button>
          }
          @if (canViewScenarioTemplateList()) {
          <button mat-menu-item (click)="selectTab(section.scenarioTemplates)">{{ section.scenarioTemplates }}</button>
          }
          @if (permissions.includes(SystemPermission.ManageTasks)) {
          <button mat-menu-item (click)="selectTab(section.taskBuilder)">{{ section.taskBuilder }}</button>
          }
          <button mat-menu-item (click)="selectTab(section.history)">{{ section.history }}</button>
        </mat-menu>
        @if (selectedSection === section.scenarios) {
        <mat-form-field class="status-field">
          <mat-label>Status</mat-label>
          <mat-select multiple [(ngModel)]="selectedStatuses">
            <mat-option value="active">Active</mat-option>
            <mat-option value="ready">Ready</mat-option>
            <mat-option value="ended">Ended</mat-option>
          </mat-select>
        </mat-form-field>
        }
        @if (selectedSection === section.taskBuilder) {
        <mat-form-field class="view-field">
          <mat-label>Select a View</mat-label>
          <mat-select [value]="selectedTaskView | async" (selectionChange)="onTaskViewChange($event)">
            @for (view of viewList | async; track view) {
            <mat-option [value]="view">{{ view.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        }
        @if (selectedSection === section.history) {
        <mat-form-field class="history-category-field">
          <mat-label>Category</mat-label>
          <mat-select [(value)]="historyView" (selectionChange)="onHistoryCategoryChange($event.value)">
            @for (hv of HistoryView | keyvalue; track hv) {
            <mat-option [value]="hv.value">{{ hv.value }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (historyView === HistoryView.user) {
        <mat-form-field class="history-select-field">
          <mat-label>Users</mat-label>
          <mat-select [(value)]="selectedHistoryUser">
            @for (user of historyUsers$ | async; track user) {
            <mat-option [value]="user">{{ user.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        }
        @if (historyView === HistoryView.view) {
        <mat-form-field class="history-select-field">
          <mat-label>Views</mat-label>
          <mat-select [(value)]="selectedHistoryView">
            @for (view of viewList | async; track view) {
            <mat-option [value]="view">{{ view.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        }
        @if (historyView === HistoryView.vm) {
        <mat-form-field class="history-select-field">
          <mat-label>VMs</mat-label>
          <mat-select [(value)]="selectedHistoryVm">
            @for (vm of historyVmList; track vm) {
            <mat-option [value]="vm">{{ vm.name }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        }
        }
        <div class="right-controls" [hidden]="selectedSection === section.taskBuilder">
          <mat-form-field class="search-field">
            <mat-label>Search</mat-label>
            <input matInput [value]="filterString" (input)="applyFilter($any($event.target).value)" />
            @if (filterString) {
            <button matSuffix mat-icon-button (click)="applyFilter('')" title="Clear Search">
              <mat-icon class="mdi-24px" fontIcon="mdi-close-circle-outline"></mat-icon>
            </button>
            }
          </mat-form-field>
        </div>
      </div>
      @if (selectedSection === section.scenarios && canViewScenarioList()) {
      <app-scenarios [filterString]="filterString" [paginator]="homePaginator"
        [selectedStatuses]="selectedStatuses"></app-scenarios>
      }
      @if (selectedSection === section.scenarioTemplates && canViewScenarioTemplateList()) {
      <app-scenario-templates [filterString]="filterString" [paginator]="homePaginator"></app-scenario-templates>
      }
      @if (selectedSection === section.taskBuilder && permissions.includes(SystemPermission.ManageTasks)) {
      <app-vm-task-execute></app-vm-task-execute>
      }
      @if (selectedSection === section.history) {
      <app-history [filterString]="filterString" [paginator]="homePaginator" [historyView]="historyView"
        [selectedUser]="selectedHistoryUser" [selectedView]="selectedHistoryView"
        [selectedVm]="selectedHistoryVm"></app-history>
      }
      <mat-paginator #homePaginator [pageSize]="10" [pageSizeOptions]="[10, 25, 50, 100]"
        [showFirstLastButtons]="true"></mat-paginator>
    </div>
  </div>
  }
</div>
